// screens/PrincipleDetailScreen.tsx — V28 S5: Appendice principle detail.
// Route: /appendice/:argomentoId/:principleId
//
// Reference reading: FULL theory INLINE (Cos'è enunciato + the "All'esame · le
// trappole" claim→verdetto rows parsed from `regola`) + the principle's sign
// media. There is NO jump-to-lesson here — Appendice is the canonical place to
// READ a rule, so the theory lives on this page (V28 build-guard §1 #4/#14).
//
// Navigation (V28 build-guard §1 #5; appendice-detail.html):
//   - TOP "Precedente / Successivo" control shows the sibling principle NAME and
//     pages between siblings IN THE SAME argomento.
//   - Gesture-back (swipe-right / left-edge tap → /appendice) + swipe-left /
//     right-edge tap → next sibling are wired globally by AppFrame via
//     `screen-flow` (back) + `nav-override` (the async next-sibling route).
//   - The ☰ hamburger stays LEFT; there is NO rendered back-arrow button.
//
// "Chiedi al Prof" stays as the ecosystem CTA: it SEEDS a question into Il Prof
// (prefill, no autoSend → the student reviews/edits → no surprise LLM call).

import type { JSX, ReactNode } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { useShellNav } from '../components/AppFrame';
import { ProfProse } from '../components/ProfProse';
import { useTokens } from '../state/theme';
import { useNavOverride } from '../state/nav-override';
import { kbApi } from '../api/kb';
import type { PrincipleDto, ArgomentoQuestionDto } from '../api/kb';
import { parseRuleRows } from '../state/appendice-rule';
import {
  appendiceApi,
  principleIdMatches,
  subSectionsForPrinciple,
  chapterContenutoToText,
  type AppendiceChapter,
  type ChapterSubSection,
  type ChapterSubSectionImage,
} from '../api/appendice';
import { anchorForSubsection } from '../utils/appendice-search';
import { QuestionProofCard } from './appendice/QuestionProofCard';
import { argomentoHue } from '../state/appendice-identity';
import { useReducedMotion } from '../state/use-reduced-motion';
import { lessonAssetSrc } from '../state/canonical-learning';

// Appendice restructure (2026-07-14): per-argomento official-questions cache
// so sibling-to-sibling navigation doesn't refetch the same list.
// Max official questions embedded inline on a sotto-argomento page — broad
// principles map dozens; the rest live in the argomento browser.
const EMBEDDED_QUESTIONS_CAP = 8;

export const argomentoQuestionsCache = new Map<string, Promise<ArgomentoQuestionDto[]>>();
function listArgomentoQuestionsCached(argomentoId: string): Promise<ArgomentoQuestionDto[]> {
  const hit = argomentoQuestionsCache.get(argomentoId);
  if (hit) return hit;
  const p = kbApi.listArgomentoQuestions(argomentoId).catch((err) => {
    argomentoQuestionsCache.delete(argomentoId);
    throw err;
  });
  argomentoQuestionsCache.set(argomentoId, p);
  return p;
}

export default function PrincipleDetailScreen(): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const { setNextOverride } = useNavOverride();
  const { argomentoId, principleId } = useParams<{
    argomentoId: string;
    principleId: string;
  }>();

  const { goBack } = useShellNav();
  const [principle, setPrinciple] = useState<PrincipleDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sibling principles in THIS argomento — carry the name so the TOP prev/next
  // control can show the sibling principle NAME (not just an id).
  const [siblings, setSiblings] = useState<{ id: string; name_it: string }[]>([]);
  // Item A (2026-06-11) — the per-argomento merged theory chapter, fetched
  // (cached) so we can render the FULL theory body of the sub-section(s) THIS
  // principle belongs to, INLINE on this page (the founder reported the detail
  // showed only "Cos'è" + the trappole, with the full theory hidden one level up
  // behind "Teoria dell'argomento"). Best-effort: a failed/empty chapter just
  // means no inline theory block (the page still renders Cos'è + trappole).
  const [chapter, setChapter] = useState<AppendiceChapter | null>(null);
  // Appendice restructure (2026-07-14): official questions for THIS
  // sotto-argomento, embedded at the end of the page (founder #4 — one
  // question style everywhere). Best-effort like the chapter.
  const [argQuestions, setArgQuestions] = useState<ArgomentoQuestionDto[] | null>(null);

  useEffect(() => {
    if (!principleId) return;
    setLoading(true);
    setError(null);
    kbApi
      .getPrinciple(principleId)
      .then((data) => setPrinciple(data))
      .catch(() => setError('Non sono riuscito a caricare questo principio. Riprova tra qualche secondo.'))
      .finally(() => setLoading(false));
  }, [principleId]);

  // Load sibling principles within the argomento (for prev/next within argomento).
  // The prototype's TOP control pages within the argomento only — there is no
  // cross-argomento jump-out here (V28-faithful).
  useEffect(() => {
    if (!argomentoId) return;
    void kbApi
      .listPrinciples(argomentoId)
      .catch(() => [] as Array<{ id: string; name_it: string }>)
      .then((principles) => {
        setSiblings(principles.map((p) => ({ id: p.id, name_it: p.name_it })));
      });
  }, [argomentoId]);

  // Item A — load the merged theory chapter for this argomento (cached, shared
  // with AppendiceTheoryScreen). Best-effort: on failure we keep `chapter` null
  // and the inline theory block simply doesn't render. Re-fetch only when the
  // argomento changes (paging between sibling principles stays within one
  // argomento, so the cache serves them all without a network hit).
  useEffect(() => {
    if (!argomentoId) return;
    let cancelled = false;
    appendiceApi
      .getChapterCached(argomentoId)
      .then((data) => { if (!cancelled) setChapter(data); })
      .catch(() => { if (!cancelled) setChapter(null); });
    return () => { cancelled = true; };
  }, [argomentoId]);

  // Official questions for the argomento (cached per argomento) — filtered to
  // this principle below via the shared short/full id matcher.
  useEffect(() => {
    if (!argomentoId) return;
    let cancelled = false;
    listArgomentoQuestionsCached(argomentoId)
      .then((qs) => { if (!cancelled) setArgQuestions(qs); })
      .catch(() => { if (!cancelled) setArgQuestions(null); });
    return () => { cancelled = true; };
  }, [argomentoId]);


  // Compute prev/next siblings within the current argomento.
  const currentIdx = principleId ? siblings.findIndex((s) => s.id === principleId) : -1;
  const prevSibling = currentIdx > 0 ? siblings[currentIdx - 1] : null;
  const nextSibling = currentIdx >= 0 && currentIdx < siblings.length - 1
    ? siblings[currentIdx + 1]
    : null;

  // Register the next-sibling route as the dynamic global "next" for the
  // nav-override registry (V29 S8: the swipe/edge-tap consumers are gone;
  // useShellNav still resolves FLOW "next" through this override). Clear on
  // unmount / when there is no next sibling so the override never leaks.
  useEffect(() => {
    const route = argomentoId && nextSibling
      ? `/appendice/${argomentoId}/${nextSibling.id}`
      : null;
    setNextOverride(route);
    return () => setNextOverride(null);
  }, [argomentoId, nextSibling, setNextOverride]);

  function gotoPrincipleInArgomento(pid: string) {
    if (!argomentoId) return;
    // History hygiene (founder r5): sibling paging REPLACES the entry so the
    // browser-back gesture never walks a sibling chain.
    navigate(`/appendice/${argomentoId}/${pid}`, { replace: true });
  }

  const argomentoName =
    principle?.argomenti?.name_it ??
    (argomentoId ? `Argomento ${argomentoId}` : 'Appendice');

  // Breadcrumb subtitle: "NN · Argomento · Regola N" (appendice-detail.html L11).
  const breadcrumb = principle
    ? `${String(principle.principle_number).padStart(2, '0')} · ${argomentoName} · Regola ${principle.principle_number}`
    : 'Principio';

  // Parse the regola into claim → verdetto rows (spec §5.6 "All'esame · le trappole").
  const ruleRows = parseRuleRows(principle?.regola);
  const hasVerdictRows = ruleRows.some((r) => r.kind === 'verdict');

  // Item A — the FULL theory sub-section(s) this principle belongs to. Bridge via
  // `matched_principle_ids` (short "P1" or full "arg1_P1" — normalized by the
  // shared helper), reusing the FP6 QuestionBrowser pattern. A principle can map
  // to MULTIPLE sub-sections; we render all, in chapter (document) order. When it
  // maps to NONE we fall back to a link into the per-argomento Teoria with the
  // nearest sub-section anchor (the first core sub-section, else the first one).
  const mappedSubSections: ChapterSubSection[] =
    argomentoId && principleId
      ? subSectionsForPrinciple(chapter, argomentoId, principleId)
      : [];
  const images = imagesForSubSections(mappedSubSections);
  const hasInlineTheory = mappedSubSections.length > 0;
  // Questions that genuinely belong to THIS principle (official mapping).
  const principleQuestions: ArgomentoQuestionDto[] =
    argomentoId && principleId && argQuestions
      ? argQuestions.filter((q) =>
          q.principle_ids.some((pid) => principleIdMatches(pid, argomentoId, principleId)))
      : [];
  // Jump-chip targets (founder #4: an index across theory / traps / questions).
  const teoriaRef = useRef<HTMLDivElement | null>(null);
  const trappoleRef = useRef<HTMLDivElement | null>(null);
  const domandeRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = useReducedMotion();
  // Restyle (2026-07-14): the parent argomento's identity hue colors this
  // page's chrome (markers, siblings, chips, card edges).
  const hue = argomentoHue(argomentoId);
  const [activeChip, setActiveChip] = useState<'teoria' | 'trappole' | 'domande'>('teoria');
  const jumpTo = (ref: { current: HTMLDivElement | null }) => {
    ref.current?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  };
  // Nearest anchor for the no-match fallback: prefer the first `core` sub-section
  // (the expanded, top-of-chapter theory), else the very first sub-section.
  const nearestSubSection: ChapterSubSection | null =
    chapter?.sub_sections.find((s) => s.tier === 'core') ??
    chapter?.sub_sections[0] ??
    null;

  // Ecosystem CTA: "Chiedi al Prof" SEEDS a question into Il Prof (prefill only,
  // no autoSend → the student reviews/edits before any send, so no surprise LLM
  // call). Chat itself is the global swipe-up sheet; this is the explicit "ask
  // about THIS principle" affordance.
  // Item A fallback — open the per-argomento Teoria, anchored at the nearest
  // sub-section, when this principle maps to none of the chapter's sub-sections.
  function goTeoria() {
    if (!argomentoId) return;
    const anchor = nearestSubSection ? anchorForSubsection(nearestSubSection.num) : null;
    const subParam = nearestSubSection ? `?sub=${encodeURIComponent(nearestSubSection.num)}` : '';
    void anchor; // the screen resolves `?sub` → anchor itself; param is the contract
    navigate(`/appendice/${argomentoId}/teoria${subParam}`);
  }

  function goChiedi() {
    if (!principle) return;
    navigate('/prof', {
      state: {
        prefill: `Spiegami questo principio: "${principle.name_it}"`,
        topicHint: principle.argomento_id
          ? { argomentoId: principle.argomento_id, argomentoName: principle.argomenti?.name_it }
          : undefined,
      },
    });
  }

  return (
    <Screen>
      <TopChrome
        title={principle?.name_it ?? argomentoName}
        subtitle={breadcrumb}
        onBack={argomentoId ? () => navigate(`/appendice/${argomentoId}`, { replace: true }) : goBack}
      />

      <main
        className="asc-scroll"
        style={{
          flex:      1,
          overflow:  'auto',
          padding:   '16px 16px 32px',
        }}
      >
        {loading && (
          <div
            style={{
              fontFamily: tokens.fonts.body,
              fontSize:   14,
              color:      tokens.textMuted,
              padding:    '24px 0',
              textAlign:  'center',
            }}
          >
            Caricamento…
          </div>
        )}

        {error && (
          <div
            style={{
              fontFamily: tokens.fonts.body,
              fontSize:   13,
              color:      tokens.wrong,
              padding:    '12px 0',
            }}
          >
            {error}
          </div>
        )}

        {!loading && principle && (
          <>
            {/* TOP "Precedente / Successivo" sibling control — VISUAL exception
                for Appendice detail (appendice-detail.html L17-33). Shows the
                sibling principle NAME; pages within the argomento. Mirrors the
                global swipe (left-edge → prev via /appendice; right-edge → next
                sibling via nav-override). */}
            {(prevSibling || nextSibling || ruleRows.length > 0 || principleQuestions.length > 0) && (
            <div
              data-testid="principle-sticky-nav"
              style={{
                position:   'sticky',
                top:        -16,
                zIndex:     6,
                background: tokens.bg,
                padding:    '10px 0 8px',
                margin:     '-10px 0 12px',
              }}
            >
            {(prevSibling || nextSibling) && (
              <div
                style={{
                  display:        'flex',
                  gap:            10,
                }}
              >
                {prevSibling ? (
                  <SiblingNavButton
                    direction="prev"
                    name={prevSibling.name_it}
                    onClick={() => gotoPrincipleInArgomento(prevSibling.id)}
                    tokens={tokens}
                    hueColor={hue.text}
                    hueBorder={hue.border}
                  />
                ) : <div style={{ flex: 1, minWidth: 0 }} />}
                {nextSibling ? (
                  <SiblingNavButton
                    direction="next"
                    name={nextSibling.name_it}
                    onClick={() => gotoPrincipleInArgomento(nextSibling.id)}
                    tokens={tokens}
                    hueColor={hue.text}
                    hueBorder={hue.border}
                  />
                ) : <div style={{ flex: 1, minWidth: 0 }} />}
              </div>
            )}

            {/* Jump chips (founder #4, 2026-07-14): an index across the page's
                sections — inside the shared sticky header with the siblings. */}
            {(ruleRows.length > 0 || principleQuestions.length > 0) && (
              <div
                data-testid="principle-jump-chips"
                style={{
                  display:   'flex',
                  gap:       8,
                  padding:   '10px 0 0',
                }}
              >
                <JumpChip tokens={tokens} active={activeChip === 'teoria'} label="La teoria" onClick={() => { setActiveChip('teoria'); jumpTo(teoriaRef); }} />
                {ruleRows.length > 0 && (
                  <JumpChip tokens={tokens} active={activeChip === 'trappole'} label="Trappole" onClick={() => { setActiveChip('trappole'); jumpTo(trappoleRef); }} />
                )}
                {principleQuestions.length > 0 && (
                  <JumpChip tokens={tokens} active={activeChip === 'domande'} label={`Domande · ${principleQuestions.length}`} onClick={() => { setActiveChip('domande'); jumpTo(domandeRef); }} />
                )}
              </div>
            )}
            </div>
            )}

            {/* Sign media frame — none (58%) / one / multi (2–4 side-by-side) */}
            <SignMediaFrame images={images} tokens={tokens} altBase={principle.name_it} />

            {/* LA TEORIA — the ONE theory home (founder #1, 2026-07-14): the
                factual enunciato leads as the opening definition, followed by
                the manual chapter sub-section(s) mapped to this principle. The
                old separate "Cos'è" heading is gone — one section, one story.
                When the principle maps to no sub-section we keep the fallback
                link into the linear chapter reader at the nearest anchor. */}
            <section ref={teoriaRef} data-testid="inline-theory" style={{ marginTop: 8, scrollMarginTop: 48 }}>
              <SectionHeading tokens={tokens} hueColor={hue.text}>La teoria</SectionHeading>
              <ProfProse text={principle.enunciato} />
              {hasInlineTheory && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 14 }}>
                  {mappedSubSections.map((s) => (
                    <InlineTheorySection key={s.num} section={s} tokens={tokens} />
                  ))}
                </div>
              )}
            </section>
            {!hasInlineTheory && chapter && nearestSubSection ? (
              <div data-testid="inline-theory-fallback" style={{ marginTop: 20 }}>
                <button
                  onClick={goTeoria}
                  style={{
                    display:      'flex',
                    alignItems:   'center',
                    gap:          10,
                    width:        '100%',
                    background:   tokens.surface,
                    border:       `1px solid ${tokens.line}`,
                    borderRadius: tokens.radius.md,
                    padding:      '12px 14px',
                    minHeight:    44,
                    cursor:       'pointer',
                    textAlign:    'left',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontFamily: tokens.fonts.body, fontSize: 13.5, fontWeight: 600, color: tokens.text, lineHeight: 1.35 }}>
                      Leggi la teoria dell&rsquo;argomento
                    </span>
                    <span style={{ display: 'block', fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.textMuted, lineHeight: 1.4, marginTop: 2 }}>
                      La spiegazione completa è nella sezione Teoria.
                    </span>
                  </span>
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke={tokens.textFaint} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ flexShrink: 0 }} aria-hidden="true"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            ) : null}

            {/* OCCHIO ALLE TRAPPOLE (founder #4, 2026-07-14): these claim →
                verdetto rows come from the regola, NOT from the official
                question pool — on the founder's phone they read as unexplained
                exam questions. The amber card + caption make them visually
                distinct from the question cards below. */}
            {ruleRows.length > 0 && (
              <div ref={trappoleRef} style={{ marginTop: 24, scrollMarginTop: 48 }}>
                <SectionHeading tokens={tokens} hueColor={hue.text}>Occhio alle trappole</SectionHeading>
                <p
                  style={{
                    margin:     '0 0 10px',
                    fontFamily: tokens.fonts.body,
                    fontSize:   12,
                    lineHeight: 1.45,
                    color:      '#7a5a1c',
                  }}
                >
                  Frasi che l&rsquo;esame usa per ingannarti — non sono domande,
                  sono i tranelli da riconoscere.
                </p>
                {hasVerdictRows ? (
                  <div
                    data-testid="trappole-rows"
                    style={{
                      display:       'flex',
                      flexDirection: 'column',
                      gap:           8,
                      background:    'rgba(212, 160, 79, 0.12)',
                      border:        '1px solid rgba(212, 160, 79, 0.45)',
                      borderRadius:  tokens.radius.md,
                      padding:       '10px 12px',
                    }}
                  >
                    {ruleRows.map((row, i) =>
                      row.kind === 'verdict' ? (
                        <VerdictRow key={i} claim={row.claim} verdict={row.verdict} note={row.note} tokens={tokens} />
                      ) : (
                        <p
                          key={i}
                          style={{
                            fontFamily: tokens.fonts.body,
                            fontSize:   13,
                            color:      tokens.textMuted,
                            lineHeight: 1.55,
                            margin:     '4px 2px',
                          }}
                        >
                          {row.text}
                        </p>
                      ),
                    )}
                  </div>
                ) : (
                  // No parseable verdicts — show the regola prose in an accented box.
                  <div
                    style={{
                      background:   tokens.accentSoft,
                      border:       `1px solid ${tokens.accent}`,
                      borderRadius: tokens.radius.lg,
                      padding:      '14px 16px',
                    }}
                  >
                    <ProfProse text={principle.regola ?? ''} serif={false} size={14} />
                  </div>
                )}
              </div>
            )}

            {/* DOMANDE D'ESAME · QUESTO SOTTO-ARGOMENTO (founder #4): the real
                official questions mapped to THIS principle, in the ONE shared
                card style (QuestionProofCard — same as the argomento browser).
                Empty state is honest; the aggregate browser stays linked. */}
            <div ref={domandeRef} data-testid="principle-domande" style={{ marginTop: 24, scrollMarginTop: 48 }}>
              <SectionHeading tokens={tokens} hueColor={hue.text}>Domande d&rsquo;esame &middot; questo sotto-argomento</SectionHeading>
              {principleQuestions.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Broad definitional principles can map DOZENS of questions
                      (arg1_P1 maps 63) — cap the embed so the page stays a
                      reading unit; the full set is one tap away. */}
                  {principleQuestions.slice(0, EMBEDDED_QUESTIONS_CAP).map((q) => (
                    <QuestionProofCard
                      key={q.id}
                      q={q}
                      argomentoId={argomentoId}
                      preferPrincipleId={principleId}
                      hueColor={hue.solid}
                    />
                  ))}
                  {principleQuestions.length > EMBEDDED_QUESTIONS_CAP && (
                    <p style={{ margin: '2px 2px 0', fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.textMuted }}>
                      Mostrate {EMBEDDED_QUESTIONS_CAP} di {principleQuestions.length} domande.
                    </p>
                  )}
                </div>
              ) : (
                <p style={{ margin: 0, fontFamily: tokens.fonts.body, fontSize: 13, color: tokens.textMuted }}>
                  Nessuna domanda ufficiale mappata su questo sotto-argomento.
                </p>
              )}
              {argomentoId && (
                <button
                  type="button"
                  data-testid="principle-domande-all"
                  onClick={() => navigate(`/appendice/${argomentoId}/domande`)}
                  style={{
                    marginTop:  10,
                    background: 'transparent',
                    border:     'none',
                    padding:    '6px 2px',
                    cursor:     'pointer',
                    fontFamily: tokens.fonts.body,
                    fontSize:   12.5,
                    fontWeight: 700,
                    color:      hue.text,
                  }}
                >
                  Tutte le domande dell&rsquo;argomento &rsaquo;
                </button>
              )}
            </div>

            {/* Ecosystem CTA — "Chiedi al Prof" seeds a question (no autoSend). */}
            <div
              data-testid="ecosystem-ctas"
              style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 10 }}
            >
              <button
                onClick={goChiedi}
                style={{
                  flex:         '1 1 160px',
                  minHeight:    44,
                  background:   tokens.surface,
                  border:       `1px solid ${tokens.accent}`,
                  borderRadius: tokens.radius.pill,
                  padding:      '11px 18px',
                  color:        tokens.accentInk,
                  fontFamily:   tokens.fonts.body,
                  fontSize:     14,
                  fontWeight:   600,
                  cursor:       'pointer',
                }}
              >
                Chiedi al Prof
              </button>
            </div>
          </>
        )}
      </main>
    </Screen>
  );
}

// Jump chip — the sotto-argomento page index (founder #4, 2026-07-14).
function JumpChip({
  tokens,
  label,
  active = false,
  onClick,
}: {
  tokens: ReturnType<typeof useTokens>;
  label: string;
  /** Restyle (2026-07-14): the active chip fills with the brand gradient. */
  active?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        fontFamily:   tokens.fonts.body,
        fontSize:     12,
        fontWeight:   700,
        padding:      '6px 13px',
        borderRadius: tokens.radius.pill,
        border:       active ? '1px solid transparent' : `1px solid ${tokens.lineStrong}`,
        background:   active ? tokens.brandGradient : tokens.surface,
        color:        active ? '#fff' : tokens.text,
        cursor:       'pointer',
        whiteSpace:   'nowrap',
      }}
    >
      {label}
    </button>
  );
}

// Small mono uppercase section heading (La teoria / Occhio alle trappole).
// Restyle (2026-07-14): with hueColor set it renders as a colored marker dot +
// label + hairline rule — the argomento's identity threading the page.
function SectionHeading({
  tokens,
  hueColor,
  children,
}: {
  tokens: ReturnType<typeof useTokens>;
  /** Marker + label color (identity hue); default stays the muted mono look. */
  hueColor?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        display:       'flex',
        alignItems:    'center',
        gap:           8,
        fontFamily:    tokens.fonts.mono,
        fontSize:      11,
        fontWeight:    hueColor ? 800 : 600,
        color:         hueColor ?? tokens.textFaint,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        margin:        '0 2px 10px',
      }}
    >
      {hueColor && (
        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 3, background: hueColor, flexShrink: 0 }} />
      )}
      <span>{children}</span>
      {hueColor && <span aria-hidden="true" style={{ flex: 1, height: 1, background: tokens.line }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item A — InlineTheorySection. The FULL theory body of ONE chapter sub-section,
// rendered EXPANDED (no collapse) inline on the principle detail page. Mirrors
// the AppendiceTheoryScreen SubSectionCard content (frozen paragraphs + ✅ VERO /
// ❌ FALSO quiz readings, spec §0.2 H3 — the two never mix and a "da scartare"
// item is framed unmistakably as FALSE), minus the collapse affordance.
// ---------------------------------------------------------------------------
function InlineTheorySection({
  section,
  tokens,
}: {
  section: ChapterSubSection;
  tokens: ReturnType<typeof useTokens>;
}): JSX.Element {
  const text = chapterContenutoToText(section.theory?.contenuto);
  const confermate = section.theory?.letture_confermate ?? [];
  const daScartare = section.theory?.letture_da_scartare ?? [];
  return (
    <div
      data-testid="inline-theory-section"
      style={{
        background:   tokens.surface,
        border:       `1px solid ${tokens.line}`,
        borderRadius: tokens.radius.md,
        padding:      '12px 14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{ flexShrink: 0, fontFamily: tokens.fonts.mono, fontSize: 11, fontWeight: 700, color: tokens.textFaint, fontVariantNumeric: 'tabular-nums' }}>
          {section.num}
        </span>
        <span style={{ fontFamily: tokens.fonts.body, fontSize: 14.5, fontWeight: 700, color: tokens.text, lineHeight: 1.3 }}>
          {section.title}
        </span>
      </div>
      {text && <ProfProse text={text} serif={false} size={14} />}

      {confermate.length > 0 && (
        <InlineQuizReadingList kind="vero" label="Vero nei quiz" items={confermate} tokens={tokens} />
      )}
      {daScartare.length > 0 && (
        <InlineQuizReadingList kind="falso" label="Falso nei quiz" items={daScartare} tokens={tokens} />
      )}
    </div>
  );
}

// A ✅ VERO / ❌ FALSO quiz-reading list for the inline theory block. The FALSO
// list is visually distinct (rose) and explicitly labelled so a "da scartare"
// item is never read as an affirmative statement (spec §0.2 H3).
function InlineQuizReadingList({
  kind,
  label,
  items,
  tokens,
}: {
  kind: 'vero' | 'falso';
  label: string;
  items: string[];
  tokens: ReturnType<typeof useTokens>;
}): JSX.Element {
  const isVero = kind === 'vero';
  const accent = isVero ? tokens.rightText : tokens.wrongText;
  const soft = isVero ? tokens.rightSoft : tokens.wrongSoft;
  return (
    <div
      data-testid={isVero ? 'inline-letture-confermate' : 'inline-letture-da-scartare'}
      style={{ marginTop: 14, background: soft, borderRadius: tokens.radius.md, padding: '10px 12px' }}
    >
      <div
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8,
          color: accent, fontFamily: tokens.fonts.mono, fontSize: 11, fontWeight: 700,
          letterSpacing: 0.4, textTransform: 'uppercase',
        }}
      >
        {isVero ? <VerdictCheckIcon /> : <VerdictCrossIcon />}
        {label}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
            <span style={{ flexShrink: 0, marginTop: 2, color: accent, display: 'inline-flex' }} aria-hidden="true">
              {isVero ? <VerdictCheckIcon /> : <VerdictCrossIcon />}
            </span>
            <span style={{ fontFamily: tokens.fonts.body, fontSize: 13.5, color: tokens.text, lineHeight: 1.5, minWidth: 0 }}>
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function subsectionImageRef(image: ChapterSubSectionImage): string | null {
  const raw = typeof image.path === 'string' && image.path.trim()
    ? image.path
    : typeof image.ref === 'string' && image.ref.trim()
      ? image.ref
      : '';
  return raw ? raw.trim() : null;
}

function imagesForSubSections(subSections: ChapterSubSection[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const section of subSections) {
    for (const image of section.images ?? []) {
      const ref = subsectionImageRef(image);
      if (!ref || seen.has(ref)) continue;
      seen.add(ref);
      out.push(ref);
    }
  }
  return out;
}

// Sign media frame: none → nothing; one → single centered with a sign-type
// caption; multi (2–4) → responsive grid (≥2 per row, stacks on the smallest
// phone).
function SignMediaFrame({
  images,
  tokens,
  altBase,
}: {
  images: string[];
  tokens: ReturnType<typeof useTokens>;
  /** Principle name → specific alt text per sign (a11y, P6 polish #4). */
  altBase?: string;
}): JSX.Element | null {
  if (!images || images.length === 0) return null;
  const single = images.length === 1;
  return (
    <div
      data-testid="sign-media"
      style={{
        display:             'grid',
        gridTemplateColumns: single ? '1fr' : 'repeat(auto-fit, minmax(120px, 1fr))',
        gap:                 12,
        justifyItems:        'center',
        marginBottom:        20,
      }}
    >
      {images.map((ref, i) => (
        <div
          key={`${ref}-${i}`}
          data-sign-frame
          style={{
            background:   tokens.surface,
            border:       `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.md,
            padding:      12,
            display:      'flex',
            flexDirection: 'column',
            alignItems:   'center',
            justifyContent: 'center',
            gap:          8,
            width:        '100%',
            maxWidth:     single ? 220 : '100%',
            minHeight:    120,
            boxSizing:    'border-box',
          }}
        >
          <img
            src={lessonAssetSrc(ref)}
            alt={
              altBase
                ? (images.length > 1 ? `Segnale ${i + 1} di ${images.length}: ${altBase}` : `Segnale: ${altBase}`)
                : 'Segnale stradale'
            }
            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
            decoding="async"
            loading="lazy"
            // If the bundled asset 404s, hide the whole frame rather than
            // leaving a broken-image icon with no explanation.
            onError={(e) => {
              const frame = e.currentTarget.closest('[data-sign-frame]');
              if (frame instanceof HTMLElement) frame.style.display = 'none';
            }}
          />
          {single && altBase && (
            <span
              style={{
                fontFamily: tokens.fonts.body,
                fontSize:   12,
                fontWeight: 600,
                color:      tokens.textFaint,
                textAlign:  'center',
                lineHeight: 1.35,
              }}
            >
              {altBase}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Inline check (✓) icon for a VERO verdict — icon + color, never color alone
 *  (WCAG 1.4.1 / dyslexia a11y), matching VeroFalsoPills + the prototype. */
function VerdictCheckIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={12}
      height={12}
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/** Inline cross (✕) icon for a FALSO verdict. */
function VerdictCrossIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={12}
      height={12}
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// A single claim → verdetto row with a VERO (sage) / FALSO (rose) icon+color chip.
function VerdictRow({
  claim,
  verdict,
  note,
  tokens,
}: {
  claim: string;
  verdict: 'VERO' | 'FALSO';
  note?: string;
  tokens: ReturnType<typeof useTokens>;
}): JSX.Element {
  const isVero = verdict === 'VERO';
  const chipBg = isVero ? tokens.rightSoft : tokens.wrongSoft;
  const chipText = isVero ? tokens.rightText : tokens.wrongText;
  return (
    <div
      style={{
        display:      'flex',
        alignItems:   'flex-start',
        gap:          10,
        background:   tokens.surface,
        border:       `1px solid ${tokens.line}`,
        borderRadius: tokens.radius.md,
        padding:      '10px 12px',
      }}
    >
      <span
        style={{
          flexShrink:    0,
          marginTop:     1,
          display:       'inline-flex',
          alignItems:    'center',
          gap:           4,
          background:    chipBg,
          color:         chipText,
          fontFamily:    tokens.fonts.mono,
          fontSize:      10,
          fontWeight:    700,
          letterSpacing: 0.5,
          padding:       '3px 8px',
          borderRadius:  tokens.radius.pill,
        }}
      >
        {isVero ? <VerdictCheckIcon /> : <VerdictCrossIcon />}
        {verdict}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontFamily: tokens.fonts.body, fontSize: 14, color: tokens.text, lineHeight: 1.45 }}>
          {claim}
        </span>
        {note && (
          <span style={{ fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.textMuted, display: 'block', marginTop: 2 }}>
            {note}
          </span>
        )}
      </span>
    </div>
  );
}

// A TOP "Precedente / Successivo" sibling button: uppercase eyebrow + chevron,
// with the sibling principle NAME below (appendice-detail.html L18-33).
function SiblingNavButton({
  direction,
  name,
  onClick,
  tokens,
  hueColor,
  hueBorder,
}: {
  direction: 'prev' | 'next';
  name: string;
  onClick: () => void;
  tokens: ReturnType<typeof useTokens>;
  /** Restyle (2026-07-14): sibling pills carry the argomento's identity hue. */
  hueColor?: string;
  hueBorder?: string;
}): JSX.Element {
  const isPrev = direction === 'prev';
  return (
    <button
      onClick={onClick}
      style={{
        flex:           1,
        // R2-1: a flex child defaults to `min-width: auto`, so a long sibling
        // name with `whiteSpace: nowrap` cannot shrink and pushes the whole row
        // past 390px (the founder had to scroll right to reach "Successivo").
        // `minWidth: 0` lets the child shrink so the name truncates instead of
        // forcing horizontal overflow. `boxSizing: border-box` keeps the 14px
        // padding inside the flex basis.
        minWidth:       0,
        boxSizing:      'border-box',
        minHeight:      54,
        display:        'flex',
        flexDirection:  'column',
        alignItems:     isPrev ? 'flex-start' : 'flex-end',
        gap:            2,
        background:     tokens.surface,
        border:         `1.5px solid ${hueBorder ?? tokens.line}`,
        borderRadius:   tokens.radius.md,
        padding:        '8px 14px',
        cursor:         'pointer',
        textAlign:      isPrev ? 'left' : 'right',
      }}
    >
      <span
        style={{
          display:        'inline-flex',
          alignItems:     'center',
          gap:            5,
          maxWidth:       '100%',
          color:          tokens.textMuted,
          fontFamily:     tokens.fonts.mono,
          fontSize:       10,
          fontWeight:     700,
          letterSpacing:  0.7,
          textTransform:  'uppercase',
        }}
      >
        {isPrev && <Chevron direction="left" />}
        {isPrev ? 'Precedente' : 'Successivo'}
        {!isPrev && <Chevron direction="right" />}
      </span>
      <span
        style={{
          // The name lives in a column flex parent (the button). With the
          // button now `minWidth: 0`, `maxWidth: 100%` + ellipsis finally
          // engages — without it the nowrap text re-widened the button.
          alignSelf:   'stretch',
          fontFamily:  tokens.fonts.body,
          fontSize:    13,
          fontWeight:  700,
          color: hueColor ?? tokens.text,
          lineHeight:  1.25,
          // Truncate a long sibling name to one tidy line.
          maxWidth:    '100%',
          overflow:    'hidden',
          textOverflow: 'ellipsis',
          whiteSpace:  'nowrap',
        }}
      >
        {name}
      </span>
    </button>
  );
}

function Chevron({ direction }: { direction: 'left' | 'right' }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={13}
      height={13}
      aria-hidden="true"
    >
      {direction === 'left'
        ? <polyline points="15 18 9 12 15 6" />
        : <polyline points="9 18 15 12 9 6" />}
    </svg>
  );
}
